import { ComponentType } from "@angular/cdk/portal";
import { Injectable } from "@angular/core";
import { MatDialog, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import { Observable } from "rxjs";

import {
  ConfirmationDialogData,
  ConfirmationDialogModalComponent,
} from "../components/common/confirmation-dialog/confirmation-dialog-modal.component";

/** Class that extends MatDialogConfig from Angular Material library
 * by adding a default settings. */
export class ModalConfig<D> extends MatDialogConfig<D> {
  width: string = "375px";
  minHeight: string = "150px";
  title?: string;
  exitAnimationDuration = 400;
  enterAnimationDuration = 400;
}

type ModalComponentType<C, D, R> = ComponentType<ModalComponent<C, D, R>>;

/**
 * Type `C` - component,
 * Type `D` - data,
 * Type `R` - result
 */
export interface ModalComponent<C, D = void, R = void> {
  dialogRef: MatDialogRef<C, R>;
  data?: D;
}

@Injectable({ providedIn: "root" })
export class ModalService {
  constructor(private dialog: MatDialog) {}

  /**
   * Open the modal with content of given component.
   * Returns the observable that completes with result of modal closing
   * activity - sends the arbitrary model from opened modal.
   * @param  {ComponentType<C>} content
   * @param  {ModalConfig} modalConfigs
   */

  openModal<C, D, R>(
    content: ModalComponentType<C, D, R>,
    modalConfigs: ModalConfig<D>,
  ): Observable<R> {
    const dialogRef = this.dialog.open(content, modalConfigs);
    return dialogRef.afterClosed();
  }

  openDefaultModal<C, D, R>(content: ModalComponentType<C, D, R>, data?: D): Observable<R> {
    const config = new ModalConfig();
    config.data = data;
    return this.openModal(content, config);
  }

  openConfirmationModal(dialogData: ConfirmationDialogData): Observable<boolean> {
    const config = new ModalConfig();
    config.data = dialogData;

    return this.openModal(ConfirmationDialogModalComponent, config);
  }

  close() {
    this.dialog.closeAll();
  }
}
